import React, { useState, useEffect } from 'react';
import usuarioService from '../services/usuarioService';
import { LuShield, LuX, LuPlus, LuPencil, LuTrash2, LuSave, LuChevronDown, LuChevronRight } from 'react-icons/lu';
import '../styles/GestionRoles.css';

const GestionRolesModal = ({ onClose, onRolesActualizados }) => {
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [permisosPorCategoria, setPermisosPorCategoria] = useState({});
  const [loading, setLoading] = useState(false);
  const [vistaActual, setVistaActual] = useState('lista');
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [categoriasExpandidas, setCategoriasExpandidas] = useState({}); // ← NUEVO

  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    nivel: 1,
    permisos: []
  });

  useEffect(() => {
    cargarRoles();
    cargarPermisos();
  }, []);

  // Inicializar todas las categorías como expandidas
  useEffect(() => {
    if (Object.keys(permisosPorCategoria).length > 0) {
      const estadoInicial = {};
      Object.keys(permisosPorCategoria).forEach(cat => {
        estadoInicial[cat] = true; // Todas expandidas por defecto
      });
      setCategoriasExpandidas(estadoInicial);
    }
  }, [permisosPorCategoria]);

  const cargarRoles = async () => {
    setLoading(true);
    try {
      const response = await usuarioService.obtenerRoles();
      if (response.success) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarPermisos = async () => {
    try {
      const response = await usuarioService.obtenerPermisos();
      if (response.success) {
        setPermisos(response.data.permisos);
        setPermisosPorCategoria(response.data.permisosPorCategoria);
      }
    } catch (error) {
      console.error('Error al cargar permisos:', error);
    }
  };

  const mostrarMensaje = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const toggleCategoria = (categoria) => {
    setCategoriasExpandidas(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };

  const handleNuevoRol = () => {
    setVistaActual('crear');
    setRolSeleccionado(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      nivel: 1,
      permisos: []
    });
  };

  const handleEditarRol = (rol) => {
    setVistaActual('editar');
    setRolSeleccionado(rol);
    setFormData({
      codigo: rol.codigo,
      nombre: rol.nombre,
      descripcion: rol.descripcion || '',
      nivel: rol.nivel,
      permisos: rol.permisos ? rol.permisos.map(p => p.id) : []
    });
  };

  const handleCancelar = () => {
    setVistaActual('lista');
    setRolSeleccionado(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      nivel: 1,
      permisos: []
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTogglePermiso = (permisoId) => {
    setFormData(prev => {
      const permisosActuales = [...prev.permisos];
      const index = permisosActuales.indexOf(permisoId);
      
      if (index > -1) {
        permisosActuales.splice(index, 1);
      } else {
        permisosActuales.push(permisoId);
      }
      
      return { ...prev, permisos: permisosActuales };
    });
  };

  const handleSeleccionarCategoria = (categoria, seleccionar) => {
    const permisosCategoria = permisosPorCategoria[categoria].map(p => p.id);
    
    setFormData(prev => {
      let permisosActuales = [...prev.permisos];
      
      if (seleccionar) {
        permisosCategoria.forEach(id => {
          if (!permisosActuales.includes(id)) {
            permisosActuales.push(id);
          }
        });
      } else {
        permisosActuales = permisosActuales.filter(id => !permisosCategoria.includes(id));
      }
      
      return { ...prev, permisos: permisosActuales };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.codigo || !formData.nombre || !formData.nivel) {
      mostrarMensaje('error', 'Complete todos los campos obligatorios');
      return;
    }

    if (formData.permisos.length === 0) {
      mostrarMensaje('error', 'Debe asignar al menos un permiso al rol');
      return;
    }

    setLoading(true);

    try {
      const response = vistaActual === 'crear'
        ? await usuarioService.crearRol(formData)
        : await usuarioService.actualizarRol(rolSeleccionado.id, formData);

      if (response.success) {
        mostrarMensaje('success', response.message);
        await cargarRoles();
        handleCancelar();
        onRolesActualizados();
      }
    } catch (error) {
      console.error('Error al guardar rol:', error);
      mostrarMensaje('error', error.message || 'Error al guardar rol');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (rol) => {
    if (!window.confirm(`¿Está seguro que desea eliminar el rol "${rol.nombre}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await usuarioService.eliminarRol(rol.id);
      if (response.success) {
        mostrarMensaje('success', response.message);
        cargarRoles();
        onRolesActualizados();
      }
    } catch (error) {
      console.error('Error al eliminar rol:', error);
      mostrarMensaje('error', error.message || 'Error al eliminar rol');
    } finally {
      setLoading(false);
    }
  };

  const categoriasSeleccionadas = (categoria) => {
    const permisosCategoria = permisosPorCategoria[categoria].map(p => p.id);
    const permisosSeleccionados = formData.permisos.filter(id => permisosCategoria.includes(id));
    return {
      todos: permisosSeleccionados.length === permisosCategoria.length,
      algunos: permisosSeleccionados.length > 0 && permisosSeleccionados.length < permisosCategoria.length,
      ninguno: permisosSeleccionados.length === 0
    };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-roles" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <LuShield />
            Gestión de Roles y Permisos
          </h3>
          <button className="btn-close" onClick={onClose}>
            <LuX />
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✓' : '⚠️'} {message.text}
          </div>
        )}

        {/* Vista Lista */}
        {vistaActual === 'lista' && (
          <div className="roles-lista">
            <div className="roles-header">
              <h4>Roles Existentes</h4>
              <button className="btn-primary" onClick={handleNuevoRol}>
                <LuPlus /> Nuevo Rol
              </button>
            </div>

            <div className="roles-grid">
              {loading ? (
                <div className="loading-cell">Cargando roles...</div>
              ) : roles.length === 0 ? (
                <div className="empty-cell">No hay roles creados</div>
              ) : (
                roles.map(rol => (
                  <div key={rol.id} className="rol-card">
                    <div className="rol-card-header">
                      <div>
                        <h5>{rol.nombre}</h5>
                        <span className="rol-nivel">Nivel {rol.nivel}</span>
                      </div>
                      <div className="rol-acciones">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEditarRol(rol)}
                          title="Editar"
                        >
                          <LuPencil />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleEliminar(rol)}
                          title="Eliminar"
                        >
                          <LuTrash2 />
                        </button>
                      </div>
                    </div>
                    <p className="rol-descripcion">{rol.descripcion || 'Sin descripción'}</p>
                    <div className="rol-permisos">
                      <strong>Permisos asignados:</strong>
                      <span className="permisos-count">
                        {rol.permisos ? rol.permisos.length : 0} permisos
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Vista Crear/Editar */}
        {(vistaActual === 'crear' || vistaActual === 'editar') && (
          <form onSubmit={handleSubmit} className="roles-form">
            <div className="form-section">
              <h4>Información del Rol</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Código <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleChange}
                    placeholder="ej: admin_local"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">Identificador único del rol</small>
                </div>

                <div className="form-group">
                  <label>
                    Nivel <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    name="nivel"
                    value={formData.nivel}
                    onChange={handleChange}
                    min="1"
                    max="10"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">1-10 (mayor = más privilegios)</small>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Nombre <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  placeholder="ej: Administrador Local"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Describe las responsabilidades de este rol..."
                  rows="3"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Permisos del Rol</h4>
              <p className="section-hint">
                Selecciona los permisos que tendrá este rol. 
                Seleccionados: <strong>{formData.permisos.length}</strong> de {permisos.length}
              </p>

              <div className="permisos-container">
                {Object.keys(permisosPorCategoria).sort().map(categoria => {
                  const estado = categoriasSeleccionadas(categoria);
                  const permisosCategoria = permisosPorCategoria[categoria];
                  const expandido = categoriasExpandidas[categoria] !== false;
                  
                  return (
                    <div key={categoria} className="categoria-permisos">
                      <div 
                        className="categoria-header"
                        onClick={() => toggleCategoria(categoria)}
                      >
                        <div className="categoria-titulo">
                          <input
                            type="checkbox"
                            checked={estado.todos}
                            ref={input => {
                              if (input) input.indeterminate = estado.algunos;
                            }}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSeleccionarCategoria(categoria, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={loading}
                          />
                          <h5>{categoria}</h5>
                          <span className="toggle-icon">
                            {expandido ? <LuChevronDown /> : <LuChevronRight />}
                          </span>
                        </div>
                        <span className="categoria-count">
                          {permisosCategoria.filter(p => formData.permisos.includes(p.id)).length} / {permisosCategoria.length}
                        </span>
                      </div>

                      {expandido && (
                        <div className="permisos-list">
                          {permisosCategoria.map(permiso => (
                            <label 
                              key={permiso.id} 
                              className={`permiso-item ${formData.permisos.includes(permiso.id) ? 'selected' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.permisos.includes(permiso.id)}
                                onChange={() => handleTogglePermiso(permiso.id)}
                                disabled={loading}
                              />
                              <div className="permiso-info">
                                <div className="permiso-nombre">{permiso.nombre}</div>
                                <div className="permiso-descripcion">{permiso.descripcion}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelar}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || formData.permisos.length === 0}
              >
                <LuSave />
                {loading ? 'Guardando...' : vistaActual === 'crear' ? 'Crear Rol' : 'Actualizar Rol'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default GestionRolesModal;